import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { Col, Divider, Row } from 'antd';
import { Form, Input } from 'formik-antd';
import { Formik, FormikProps } from 'formik';
import { Link } from 'react-router-dom';
import { map } from 'lodash';
import * as Yup from 'yup';

import {
  citiesSelector,
  countriesSelector,
  countryCitiesSelector,
  isCitiesLoadingSelector,
  isCountriesLoadingSelector,
  isCountryCitiesLoadingSelector,
  isStatesLoadingSelector,
  statesSelector,
} from 'redux/selectors/regionSelectors';
import { userSelector } from 'redux/selectors/authenticationSelectors';
import { urlRegex } from 'utils/urlUtils';
import Radio from 'components/formik/Radio';
import { getInitialCompanyName } from 'utils/companyUtils';
import FormItem from 'components/formik/FormItem';
import Checkbox from 'components/formik/Checkbox';
import { CompanyProfileFormValues } from './mapUtils';
import FormikEffect from 'components/formik/FormikEffect';
import {
  FullBOUserResponseModel,
  ProfileTypeEnum,
} from 'api/backofficeApiClient';
import { getAllIndustries } from 'redux/reducers/qualificationSlice';
import { getAllCities, getAllCountries, getAllCountryCities, getAllStates } from 'redux/reducers/regionSlice';
import {
  industriesSelector,
  isIndustriesLoadingSelector,
} from 'redux/selectors/qualificationSelectors';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { countryUSAid } from 'constants/index';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';

type Props = {
  formRef: any;
  isActive?: boolean;
  formFooter?: ReactNode;
  profileInfo?: FullBOUserResponseModel;
  onSubmit?: (values: CompanyProfileFormValues) => void;
  onChange?: (formik: FormikProps<CompanyProfileFormValues>) => void;
  profileType: ProfileTypeEnum;
};

const getInitialValidationSchema = (isCorporateTA: boolean) => {
  const schema: any = {
    ownerInfo: Yup.object({
      firstName: Yup.string()
        .trim()
        .required('Field is required')
        .max(120, 'First name should be 120 characters maximum'),
      lastName: Yup.string()
        .trim()
        .required('Field is required')
        .max(120, 'Last name should be 120 characters maximum'),
      email: Yup.string()
        .email('Email format is invalid')
        .required('Field is required')
        .max(120, 'Email should be 120 characters maximum'),
    }),
    bestContact: Yup.object({
      sameAsOwner: Yup.boolean(),
      firstName: Yup.string()
        .nullable()
        .trim()
        .when('sameAsOwner', {
          is: (sameAsOwner: boolean) => !sameAsOwner,
          then: Yup.string()
            .max(120, 'First name should be 120 characters maximum')
            .required('Field is required'),
        }),
      lastName: Yup.string()
        .nullable()
        .trim()
        .when('sameAsOwner', {
          is: (sameAsOwner: boolean) => !sameAsOwner,
          then: Yup.string()
            .required('Field is required')
            .max(120, 'Last name should be 120 characters maximum'),
        }),
    }),
    emailForCommunications: Yup.object({
      sameAsOwner: Yup.boolean(),
      email: Yup.string().when('sameAsOwner', {
        is: (sameAsOwner: boolean) => !sameAsOwner,
        then: Yup.string()
          .email('Email format is invalid')
          .max(120, 'Email should be 120 characters maximum')
          .required('Field is required'),
      }),
    }),
    businessName: Yup.string()
      .trim()
      .required('Field is required')
      .max(250, 'Business name should be 250 characters maximum'),
    businessPhone: Yup.string()
      .trim()
      .required('Field is required')
      .max(120, 'Phone number should be 120 characters maximum'),
    country: Yup.number().required('Field is required'),
    state: Yup.string()
      .trim()
      .max(120, 'State should be 120 characters maximum')
      .when('country', {
        is: (country: number) => checkIsCountriesWithStatesDropdown(country),
        then: Yup.string().required('Field is required'),
      }),
    city: Yup.string()
      .trim()
      .max(120, 'City should be 120 characters maximum')
      .when('country', {
        is: (country: number) => checkIsCountriesWithStatesDropdown(country),
        then: Yup.string().required('Field is required'),
      }),
    internationalStateName: Yup.string()
      .trim()
      .max(120, 'State should be 120 characters maximum')
      .when('country', {
        is: (country: number) => !checkIsCountriesWithStatesDropdown(country),
        then: Yup.string().required('Field is required'),
      }),
    internationalCityName: Yup.string()
      .trim()
      .max(120, 'City should be 120 characters maximum')
      .when('country', {
        is: (country: number) => !checkIsCountriesWithStatesDropdown(country),
        then: Yup.string().required('Field is required'),
      }),
    address: Yup.string()
      .trim()
      .required('Field is required')
      .max(120, `Address should be 120 characters maximum`),
    zip: Yup.string()
      .trim()
      .max(120, 'ZIP code should be 120 characters maximum'),
    industry: Yup.array().of(Yup.string()),
  };

  if (isCorporateTA) {
    schema.businessEmail = Yup.string()
      .email('Email format is invalid')
      .max(120, 'Email should be 120 characters maximum')
      .required('Field is required');
  }

  return schema;
};

const getCreationValidationSchema = (initialValidationSchema: any) =>
  Yup.object({
    ...initialValidationSchema,
    assignmentLocations: Yup.array()
      .of(Yup.number().defined())
      .min(1, 'Field is required'),
    assignment: Yup.boolean(),
    termsAndConditions: Yup.boolean().when('isRegisteredOnMarketPlace', {
      is: true,
      then: Yup.boolean().oneOf(
        [true],
        'Please review and accept the myBasePay Terms and Conditions before proceeding',
      ),
    }),
  });

const getEditValidationSchema = (initialValidationSchema: any) =>
  Yup.object({
    ...initialValidationSchema,
    website: Yup.string()
      .matches(urlRegex, 'Website is invalid')
      .max(120, 'Website should be 120 characters maximum'),
  });

export default function CompanyProfileForm(props: Props) {
  const {
    formRef,
    onSubmit,
    onChange,
    formFooter,
    profileInfo,
    isActive = false,
    profileType,
  } = props;
  const isEdit = !!profileInfo;
  const dispatch = useAppDispatch();
  const user = useAppSelector(userSelector);
  const states = useAppSelector(statesSelector);
  const isStatesLoading = useAppSelector(isStatesLoadingSelector);
  const cities = useAppSelector(citiesSelector);
  const isCitiesLoading = useAppSelector(isCitiesLoadingSelector);
  const countries = useAppSelector(countriesSelector);
  const isCountriesLoading = useAppSelector(isCountriesLoadingSelector);
  const countryCities = useAppSelector(countryCitiesSelector);
  const isCountryCitiesLoading = useAppSelector(isCountryCitiesLoadingSelector);
  const industries = useAppSelector(industriesSelector);
  const isIndustriesLoading = useAppSelector(isIndustriesLoadingSelector);
  const initialValues = getInitialValues();
  const [selectedState, setSelectedState] = useState(initialValues.state);
  const [selectedCountry, setSelectedCountry] = useState(initialValues.country);

  const filteredStates = useMemo(
    () => states.filter((state) => state.countryId === selectedCountry),
    [states, selectedCountry],
  );

  const statesForAssignmentLocations = useMemo(
    () => states.filter((state) => state.countryId === countryUSAid),
    [states],
  );

  const filteredInternationalCities = useMemo(
    () => countryCities.filter((city) => city.countryId === selectedCountry),
    [selectedCountry, countryCities],
  );

  const filteredCities = useMemo(
    () => cities.filter((city) => city.stateId === selectedState),
    [selectedState, cities],
  );

  const isCorporateTAProfileType = profileType === ProfileTypeEnum.Corporate;

  const initialValidationSchema = getInitialValidationSchema(
    isCorporateTAProfileType,
  );
  const creationValidationSchema = getCreationValidationSchema(
    initialValidationSchema,
  );
  const editValidationSchema = getEditValidationSchema(initialValidationSchema);

  useEffect(() => {
    dispatch(getAllCountries());
    dispatch(getAllCities());
    dispatch(getAllCountryCities());
    dispatch(getAllStates());
    dispatch(getAllIndustries());
  }, [dispatch]);

  function getInitialValues(): CompanyProfileFormValues {
    if (!isEdit) {
      return {
        ownerInfo: {
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          email: user?.email || '',
        },
        bestContact: {
          sameAsOwner: true,
          firstName: '',
          lastName: '',
        },
        emailForCommunications: {
          sameAsOwner: true,
          email: '',
        },
        businessName: getInitialCompanyName(user),
        businessPhone: '',
        country: countryUSAid,
        state: undefined,
        internationalStateName: undefined,
        city: '',
        internationalCityName: undefined,
        address: '',
        zip: '',
        businessEmail: user?.email || '',
        industry: [],
        assignmentLocations: [],
        assignment: true,
        termsAndConditions: false,
        isRegisteredOnMarketPlace: user?.isRegisteredOnMarketPlace,
      };
    }

    const { ownerInfo, bestContact, businessInfo, website } =
      profileInfo as FullBOUserResponseModel;

    return {
      ownerInfo: {
        firstName: ownerInfo?.firstName || '',
        lastName: ownerInfo?.lastName || '',
        email: ownerInfo?.email || '',
      },
      bestContact: {
        ...bestContact,
        sameAsOwner:
          ownerInfo?.firstName === bestContact?.firstName &&
          ownerInfo?.lastName === bestContact?.lastName,
      },
      emailForCommunications: {
        email: bestContact?.email,
        sameAsOwner: ownerInfo?.email === bestContact?.email,
      },
      businessName: businessInfo?.name || '',
      businessPhone: businessInfo?.phoneNumber || '',
      country: businessInfo?.location?.countryId || countryUSAid,
      city: businessInfo?.location?.city || '',
      state: businessInfo?.location?.stateId || undefined,
      internationalStateName:
        businessInfo?.location?.internationalStateName || undefined,
      internationalCityName:
        businessInfo?.location?.internationalCityName || undefined,
      address: businessInfo?.location?.address || '',
      zip: businessInfo?.location?.zip || '',
      businessEmail: businessInfo?.businessEmail || '',
      website: website || '',
      industry: map(
        businessInfo?.industries,
        (industry) => industry.name,
      ) as string[],
    };
  }

  const handleStateChange = (
    stateId: number,
    formik: FormikProps<CompanyProfileFormValues>,
  ) => {
    setSelectedState(stateId);

    formik.setFieldValue('city', '', false);
    formik.setFieldValue('internationalCityName', '', false);
  };

  const handleBestContactNameAsOwnerChange = (
    e: any,
    formik: FormikProps<CompanyProfileFormValues>,
  ) => {
    if (e.target.checked) {
      return;
    }

    formik.setFieldValue('bestContact.firstName', '', false);
    formik.setFieldValue('bestContact.lastName', '', false);
  };

  const handleContactEmailSameAsOwnerChange = (
    e: any,
    formik: FormikProps<CompanyProfileFormValues>,
  ) => {
    if (e.target.checked) {
      return;
    }

    formik.setFieldValue('emailForCommunications.email', '', false);
  };

  const handleCountryChange = (
    countryId: number,
    formik: FormikProps<CompanyProfileFormValues>,
  ) => {
    setSelectedCountry(countryId);

    formik.setFieldValue('city', '', false);
    formik.setFieldValue('internationalStateName', '', false);
    formik.setFieldValue('internationalCityName', '', false);
    formik.setFieldValue('state', '', false);
    formik.setFieldValue('businessPhone', '', false);
  };

  return (
    <Formik
      enableReinitialize
      innerRef={formRef}
      initialValues={initialValues}
      onSubmit={onSubmit ? onSubmit : () => {}}
      validationSchema={
        isEdit ? editValidationSchema : creationValidationSchema
      }
    >
      {(formik) => (
        <>
          {onChange && (
            <FormikEffect onChange={onChange} disableOnChange={!isActive} />
          )}

          <Form layout="vertical">
            <div className="modal-scroll-wrapper">
              <div className="modal-title">Company representative</div>

              <Row gutter={40}>
                <Col xs={24} md={12}>
                  <Row gutter={20}>
                    <Col xs={24} md={12}>
                      <FormItem name="ownerInfo.firstName" label="First name *">
                        <Input
                          name="ownerInfo.firstName"
                          placeholder="Enter first name"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>

                    <Col xs={24} md={12}>
                      <FormItem name="ownerInfo.lastName" label="Last name *">
                        <Input
                          name="ownerInfo.lastName"
                          placeholder="Enter last name"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>
                  </Row>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem name="ownerInfo.email" label="Email *">
                    <Input name="ownerInfo.email" placeholder="Enter email" autoComplete="off" />
                  </FormItem>
                </Col>
              </Row>

              <Divider className="modal-divider after-form" />

              <div className="modal-title">Best point of contact</div>

              <Row gutter={40}>
                <Col xs={24} md={12}>
                  <Row gutter={20}>
                    <Col xs={24}>
                      <FormItem
                        name="bestContact.sameAsOwner"
                        className="checkbox-wrapper"
                      >
                        <Checkbox
                          name="bestContact.sameAsOwner"
                          onChange={(e) =>
                            handleBestContactNameAsOwnerChange(e, formik)
                          }
                        >
                          The same as above
                        </Checkbox>
                      </FormItem>
                    </Col>

                    <Col xs={24} md={12}>
                      <FormItem
                        name="bestContact.firstName"
                        label="First name *"
                      >
                        <Input
                          value={
                            formik.values.bestContact.sameAsOwner
                              ? formik.values.ownerInfo.firstName
                              : formik.values.bestContact.firstName
                          }
                          name="bestContact.firstName"
                          disabled={formik.values.bestContact.sameAsOwner}
                          placeholder="Enter first name"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>

                    <Col xs={24} md={12}>
                      <FormItem name="bestContact.lastName" label="Last name *">
                        <Input
                          value={
                            formik.values.bestContact.sameAsOwner
                              ? formik.values.ownerInfo.lastName
                              : formik.values.bestContact.lastName
                          }
                          name="bestContact.lastName"
                          disabled={formik.values.bestContact.sameAsOwner}
                          placeholder="Enter last name"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>
                  </Row>
                </Col>

                <Col xs={24} md={12}>
                  <Row gutter={20}>
                    <Col xs={24}>
                      <FormItem
                        name="emailForCommunications.sameAsOwner"
                        className="checkbox-wrapper"
                      >
                        <Checkbox
                          name="emailForCommunications.sameAsOwner"
                          onChange={(e) =>
                            handleContactEmailSameAsOwnerChange(e, formik)
                          }
                        >
                          The same as above
                        </Checkbox>
                      </FormItem>
                    </Col>

                    <Col xs={24}>
                      <FormItem
                        name="emailForCommunications.email"
                        label="Email for communications *"
                      >
                        <Input
                          value={
                            formik.values.emailForCommunications.sameAsOwner
                              ? formik.values.ownerInfo.email
                              : formik.values.emailForCommunications.email
                          }
                          name="emailForCommunications.email"
                          disabled={
                            formik.values.emailForCommunications.sameAsOwner
                          }
                          placeholder="Enter email for communications"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Divider className="modal-divider after-form" />

              <div className="modal-title">Business information</div>

              <Row gutter={40}>
                <Col xs={24} md={12}>
                  <Row gutter={20}>
                    <Col xs={24} md={12}>
                      <FormItem name="businessName" label="Business name *">
                        <Input
                          name="businessName"
                          placeholder="Enter business name"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>

                    {isCorporateTAProfileType ? (
                      <Col xs={24} md={12}>
                        <FormItem name="businessEmail" label="Business email *">
                          <Input
                            name="businessEmail"
                            placeholder="Enter business email"
                            autoComplete="off"
                          />
                        </FormItem>
                      </Col>
                    ) : (
                      <Col xs={24} md={12}>
                        <FormItem
                          label="Business phone number *"
                          name="businessPhone"
                        >
                          <Input
                            name="businessPhone"
                            placeholder="Enter phone number"
                            autoComplete="off"
                          />
                        </FormItem>
                      </Col>
                    )}
                  </Row>
                </Col>

                {isCorporateTAProfileType ? (
                  <Col xs={24} md={12}>
                    <Row gutter={20}>
                      <Col xs={24} md={12}>
                        <FormItem
                          label="Business phone number *"
                          name="businessPhone"
                        >
                          <Input
                            name="businessPhone"
                            placeholder="Enter phone number"
                            autoComplete="off"
                          />
                        </FormItem>
                      </Col>

                      <Col xs={24} md={12}>
                        <FormItem name="industry" label="Industry">
                          <FormikSelectWithLoading
                            isLoading={isIndustriesLoading}
                            name="industry"
                            showSearch
                            mode="tags"
                            placeholder="Select industry"
                            optionFilterProp="children"
                          >
                            {industries.map((industry) => (
                              <FormikSelectWithLoading.Option
                                key={`industry-${industry.id}`}
                                value={industry.name || ''}
                              >
                                {industry.name}
                              </FormikSelectWithLoading.Option>
                            ))}
                          </FormikSelectWithLoading>
                        </FormItem>
                      </Col>
                    </Row>
                  </Col>
                ) : (
                  <Col xs={24} md={12}>
                    <FormItem name="industry" label="Industry">
                      <FormikSelectWithLoading
                        isLoading={isIndustriesLoading}
                        name="industry"
                        showSearch
                        mode="tags"
                        placeholder="Select industry"
                        optionFilterProp="children"
                      >
                        {industries.map((industry) => (
                          <FormikSelectWithLoading.Option
                            key={`industry-${industry.id}`}
                            value={industry.name || ''}
                          >
                            {industry.name}
                          </FormikSelectWithLoading.Option>
                        ))}
                      </FormikSelectWithLoading>
                    </FormItem>
                  </Col>
                )}
              </Row>

              <Row gutter={40}>
                <Col xs={24} md={12}>
                  <Row gutter={20}>
                    <Col xs={24} md={12}>
                      <FormItem name="country" label="Country *">
                        <FormikSelectWithLoading
                          isLoading={isCountriesLoading}
                          name="country"
                          showSearch
                          placeholder="Select head office country"
                          optionFilterProp="children"
                          onChange={(value: number) =>
                            handleCountryChange(value, formik)
                          }
                        >
                          {countries.map((country) => (
                            <FormikSelectWithLoading.Option
                              key={`select-country-${country.id}`}
                              value={country.id || -1}
                            >
                              {country.name}
                            </FormikSelectWithLoading.Option>
                          ))}
                        </FormikSelectWithLoading>
                      </FormItem>
                    </Col>

                    <Col xs={24} md={12}>
                      {checkIsCountriesWithStatesDropdown(
                        formik.values.country,
                      ) ? (
                        <FormItem name="state" label="State *">
                          <FormikSelectWithLoading
                            isLoading={isStatesLoading}
                            name="state"
                            showSearch
                            placeholder="Select head office state"
                            optionFilterProp="children"
                            onChange={(value: number) =>
                              handleStateChange(value, formik)
                            }
                          >
                            {filteredStates.map((state) => (
                              <FormikSelectWithLoading.Option
                                key={`select-state-${state.id}`}
                                value={state.id || -1}
                              >
                                {state.name}
                              </FormikSelectWithLoading.Option>
                            ))}
                          </FormikSelectWithLoading>
                        </FormItem>
                      ) : (
                        <FormItem name="internationalStateName" label="State *">
                          <Input
                            name="internationalStateName"
                            placeholder="Enter head office state"
                            disabled={!formik.values.country}
                            autoComplete="off"
                          />
                        </FormItem>
                      )}
                    </Col>

                    <Col xs={24} md={12}>
                      {checkIsCountriesWithStatesDropdown(
                        formik.values.country,
                      ) ? (
                        <FormItem name="city" label="City *">
                          <FormikAutoCompleteWithLoading
                            isLoading={isCitiesLoading}
                            name="city"
                            filterOption
                            disabled={!formik.values.state}
                            placeholder="Enter head office city"
                            showSearch
                          >
                            {filteredCities.map((city) => (
                              <FormikAutoCompleteWithLoading.Option
                                key={`city-state-${city.id}`}
                                value={city.name || ''}
                              >
                                {city.name}
                              </FormikAutoCompleteWithLoading.Option>
                            ))}
                          </FormikAutoCompleteWithLoading>
                        </FormItem>
                      ) : (
                        <FormItem name="internationalCityName" label="City *">
                          <FormikAutoCompleteWithLoading
                            isLoading={isCountryCitiesLoading}
                            name="internationalCityName"
                            filterOption
                            placeholder="Enter head office city"
                            showSearch
                            disabled={!formik.values.internationalStateName}
                          >
                            {filteredInternationalCities.map((city) => (
                              <FormikAutoCompleteWithLoading.Option
                                key={`company-city-${city.id}`}
                                value={city.name || ''}
                              >
                                {city.name}
                              </FormikAutoCompleteWithLoading.Option>
                            ))}
                          </FormikAutoCompleteWithLoading>
                        </FormItem>
                      )}
                    </Col>
                  </Row>
                </Col>

                <Col xs={24} md={12}>
                  <Row gutter={20}>
                    <Col xs={24} md={12}>
                      <FormItem label="Address *" name="address">
                        <Input name="address" placeholder="Enter address" autoComplete="off" />
                      </FormItem>
                    </Col>

                    <Col xs={24} md={12}>
                      <FormItem label="ZIP code" name="zip">
                        <Input
                          name="zip"
                          className="full-width"
                          placeholder="Enter ZIP code"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>
                  </Row>
                </Col>
              </Row>

              {!isEdit && (
                <Row gutter={40} align="middle">
                  <Col xs={24} md={12}>
                    <FormItem
                      name="assignmentLocations"
                      label="Assignment locations *"
                    >
                      <FormikSelectWithLoading
                        isLoading={isStatesLoading}
                        name="assignmentLocations"
                        showSearch
                        mode="multiple"
                        placeholder="Select states"
                        optionFilterProp="children"
                      >
                        {statesForAssignmentLocations.map((state) => (
                          <FormikSelectWithLoading.Option
                            key={`select-place-${state.id}`}
                            value={state.id || -1}
                          >
                            {state.name}
                          </FormikSelectWithLoading.Option>
                        ))}
                      </FormikSelectWithLoading>
                    </FormItem>
                  </Col>

                  <Col xs={24} md={12}>
                    <span className="hidden-sm-down">
                      <br />
                    </span>

                    <FormItem
                      className="inline-radio"
                      name="assignment"
                      data-id="initialUserAssignmentField"
                      label="Do you have an upcoming assignment?"
                    >
                      <Radio.Group
                        className="radio"
                        name="assignment"
                        options={[
                          { label: 'Yes', value: true },
                          { label: 'No', value: false },
                        ]}
                      />
                    </FormItem>
                  </Col>

                  {formik.values.isRegisteredOnMarketPlace && (
                    <Col xs={24}>
                      <FormItem
                        name="termsAndConditions"
                        className="checkbox-wrapper"
                      >
                        <Checkbox name="termsAndConditions">
                          <span>I understand and agree to the MyBasePay </span>

                          <Link to="/terms-and-conditions" target="_blank">
                            Terms and Conditions.
                          </Link>
                        </Checkbox>
                      </FormItem>
                    </Col>
                  )}
                </Row>
              )}

              {isEdit && (
                <Col xs={24} md={12}>
                  <FormItem name="website" label="Website">
                    <Input name="website" placeholder="Enter the website" autoComplete="off" />
                  </FormItem>
                </Col>
              )}
            </div>

            {!!formFooter && formFooter}
          </Form>
        </>
      )}
    </Formik>
  );
}
